import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  HelpCircle, 
  Send, 
  Loader2, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Bug,
  CreditCard,
  Lightbulb,
  Wrench,
  MoreHorizontal,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// تصنيفات الدعم
const CATEGORIES = [
  { value: "technical", label: "مشكلة تقنية", icon: Wrench, color: "text-blue-400" },
  { value: "billing", label: "الدفع والاشتراك", icon: CreditCard, color: "text-emerald-400" },
  { value: "feature", label: "طلب ميزة جديدة", icon: Lightbulb, color: "text-amber-400" },
  { value: "bug", label: "الإبلاغ عن خطأ", icon: Bug, color: "text-red-400" },
  { value: "other", label: "أخرى", icon: MoreHorizontal, color: "text-slate-400" },
];

// مستويات الأولوية
const PRIORITIES = [
  { value: "low", label: "منخفضة", color: "bg-slate-500" },
  { value: "medium", label: "متوسطة", color: "bg-amber-500" },
  { value: "high", label: "عالية", color: "bg-red-500" },
];

// حالات الطلب
const STATUS_MAP = {
  open: { label: "مفتوح", color: "bg-amber-500", icon: Clock },
  in_progress: { label: "قيد المعالجة", color: "bg-blue-500", icon: Loader2 },
  closed: { label: "مغلق", color: "bg-emerald-500", icon: CheckCircle },
};

export default function SupportPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('new'); // new | tickets
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [expandedTicket, setExpandedTicket] = useState(null);
  
  // نموذج طلب جديد
  const [formData, setFormData] = useState({
    subject: '',
    category: 'technical',
    description: '',
    priority: 'medium',
  });

  // جلب الطلبات
  const fetchTickets = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/support/my-tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTickets(data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets();
    }
  }, [activeTab, fetchTickets]);

  // إرسال طلب جديد
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/support/create-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`تم إرسال طلب الدعم - رقم الطلب: ${data.ticket_number}`);
        setFormData({
          subject: '',
          category: 'technical',
          description: '',
          priority: 'medium',
        });
        setActiveTab('tickets');
        fetchTickets();
      } else {
        toast.error(data.message || "حدث خطأ");
      }
    } catch (error) {
      toast.error("فشل إرسال الطلب");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 p-4 md:p-6" dir="rtl">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">الدعم الفني</h1>
          <p className="text-slate-400 mt-2">نحن هنا لمساعدتك</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button 
            variant={activeTab === 'new' ? 'default' : 'outline'}
            onClick={() => setActiveTab('new')}
            className={activeTab === 'new' ? 'bg-blue-600' : 'border-slate-600'}
          >
            <Send className="w-4 h-4 me-2" />
            طلب جديد
          </Button>
          <Button 
            variant={activeTab === 'tickets' ? 'default' : 'outline'}
            onClick={() => setActiveTab('tickets')}
            className={activeTab === 'tickets' ? 'bg-blue-600' : 'border-slate-600'}
          >
            <MessageSquare className="w-4 h-4 me-2" />
            طلباتي ({tickets.length})
          </Button>
        </div>

        {/* New Ticket Form */}
        {activeTab === 'new' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-400" />
                إرسال طلب دعم جديد
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* العنوان */}
                <div>
                  <Label className="text-slate-300">عنوان المشكلة *</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    placeholder="اكتب عنوان موجز للمشكلة"
                    required
                  />
                </div>

                {/* التصنيف */}
                <div>
                  <Label className="text-slate-300">التصنيف *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setFormData({...formData, category: cat.value})}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            formData.category === cat.value
                              ? 'border-blue-500 bg-blue-500/20'
                              : 'border-slate-600 bg-slate-900 hover:border-slate-500'
                          }`}
                        >
                          <Icon className={`w-5 h-5 mx-auto mb-1 ${cat.color}`} />
                          <span className="text-xs text-slate-300">{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* الأولوية */}
                <div>
                  <Label className="text-slate-300">الأولوية</Label>
                  <div className="flex gap-2 mt-2">
                    {PRIORITIES.map((pri) => (
                      <button
                        key={pri.value}
                        type="button"
                        onClick={() => setFormData({...formData, priority: pri.value})}
                        className={`flex-1 py-2 px-4 rounded-lg border text-center transition-all ${
                          formData.priority === pri.value
                            ? 'border-blue-500 bg-blue-500/20'
                            : 'border-slate-600 bg-slate-900 hover:border-slate-500'
                        }`}
                      >
                        <span className={`inline-block w-2 h-2 rounded-full ${pri.color} me-2`}></span>
                        <span className="text-sm text-slate-300">{pri.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* الوصف */}
                <div>
                  <Label className="text-slate-300">وصف المشكلة *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white mt-1 min-h-[150px]"
                    placeholder="اشرح المشكلة بالتفصيل... ما الذي حدث؟ متى حدث؟ ما هي الخطوات التي قمت بها؟"
                    required
                  />
                </div>

                {/* زر الإرسال */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-4"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 me-2 animate-spin" />
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 me-2" />
                      إرسال طلب الدعم
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* My Tickets */}
        {activeTab === 'tickets' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchTickets}
                className="border-slate-600"
              >
                <RefreshCw className="w-4 h-4 me-2" />
                تحديث
              </Button>
            </div>

            {tickets.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">لا توجد طلبات دعم</p>
                  <Button
                    onClick={() => setActiveTab('new')}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    <Send className="w-4 h-4 me-2" />
                    إرسال طلب جديد
                  </Button>
                </CardContent>
              </Card>
            ) : (
              tickets.map((ticket) => {
                const status = STATUS_MAP[ticket.status] || STATUS_MAP.open;
                const StatusIcon = status.icon;
                const category = CATEGORIES.find(c => c.value === ticket.category);
                const CategoryIcon = category?.icon || HelpCircle;
                const isExpanded = expandedTicket === ticket.ticket_number;

                return (
                  <Card 
                    key={ticket.ticket_number} 
                    className={`bg-slate-800 border-slate-700 ${
                      ticket.status === 'open' ? 'border-r-4 border-r-amber-500' : ''
                    }`}
                  >
                    <CardContent className="p-4">
                      {/* Header */}
                      <div 
                        className="flex justify-between items-start cursor-pointer"
                        onClick={() => setExpandedTicket(isExpanded ? null : ticket.ticket_number)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-slate-500 font-mono">
                              {ticket.ticket_number}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${status.color} text-white`}>
                              {status.label}
                            </span>
                          </div>
                          <h3 className="text-white font-bold flex items-center gap-2">
                            <CategoryIcon className={`w-4 h-4 ${category?.color || 'text-slate-400'}`} />
                            {ticket.subject}
                          </h3>
                          <p className="text-slate-500 text-xs mt-1">
                            {new Date(ticket.created_at).toLocaleString('ar-IQ')}
                          </p>
                        </div>
                        <button className="text-slate-400 hover:text-white p-2">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
                          {/* الوصف */}
                          <div className="bg-slate-900 rounded-lg p-3">
                            <p className="text-slate-300 text-sm whitespace-pre-wrap">
                              {ticket.description}
                            </p>
                          </div>

                          {/* الردود */}
                          {ticket.responses && ticket.responses.length > 0 && (
                            <div className="space-y-3">
                              <h4 className="text-white font-bold text-sm">الردود:</h4>
                              {ticket.responses.map((response, idx) => (
                                <div 
                                  key={idx}
                                  className={`rounded-lg p-3 ${
                                    response.from === 'support' 
                                      ? 'bg-blue-500/10 border border-blue-500/30' 
                                      : 'bg-slate-900'
                                  }`}
                                >
                                  <p className="text-xs text-slate-500 mb-1">
                                    {response.from === 'support' ? 'الدعم الفني' : 'أنت'} - 
                                    {new Date(response.created_at).toLocaleString('ar-IQ')}
                                  </p>
                                  <p className="text-slate-300 text-sm">
                                    {response.message}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* حالة الانتظار */}
                          {ticket.status === 'open' && (!ticket.responses || ticket.responses.length === 0) && (
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                              <Clock className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                              <p className="text-amber-300 text-sm">
                                طلبك قيد المراجعة - سيتم الرد قريباً
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
